import { useState, useRef, useEffect, useLayoutEffect } from "react";
import { MoreVertical, Trash2, Pencil } from "lucide-react";
import { useAuth, API } from "../App";
import axios from "axios";
import { toast } from "sonner";

/**
 * Discreet "Manage" kebab menu visible ONLY to founder + admin accounts.
 *
 * Use this on every piece of moderable content (discussion cards, replies,
 * events, clinic reviews, social posts, partners, resources, members, etc.).
 * Regular members never see it because we hard-gate on `user?.is_admin`.
 *
 * Usage:
 *   <AdminManageMenu
 *     resourceType="discussion"
 *     resourceId={d.discussion_id}
 *     resourceLabel={d.title}
 *     onDeleted={() => fetchDiscussions()}
 *   />
 *
 * `resourceType` maps to the `/api/admin/moderation/{type}/{id}` route segment:
 *   discussion | reply | event | clinic-review | social-post | social-comment |
 *   resource   | newgrad-resource | partner | user | category
 *
 * Optional `extraActions` lets pages plug in things like "Edit" for events:
 *   extraActions={[{ label: "Edit details", icon: Pencil, onClick: () => setEditing(evt) }]}
 */
export const AdminManageMenu = ({
  resourceType,
  resourceId,
  resourceLabel = "this item",
  onDeleted,
  extraActions = [],
  size = "sm",
  align = "right",
  testIdPrefix,
}) => {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [openUp, setOpenUp] = useState(false);
  const wrapperRef = useRef(null);
  const popoverRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    const onEsc = (e) => { if (e.key === "Escape") setOpen(false); };
    const onScroll = () => setOpen(false);
    document.addEventListener("mousedown", onOutside);
    document.addEventListener("touchstart", onOutside);
    document.addEventListener("keydown", onEsc);
    window.addEventListener("scroll", onScroll, true);
    return () => {
      document.removeEventListener("mousedown", onOutside);
      document.removeEventListener("touchstart", onOutside);
      document.removeEventListener("keydown", onEsc);
      window.removeEventListener("scroll", onScroll, true);
    };
  }, [open]);

  // Decide whether the popover should open upward (when there's no room
  // below the kebab) or downward (default). Runs every time the menu opens.
  useLayoutEffect(() => {
    if (!open || !wrapperRef.current) return;
    const rect = wrapperRef.current.getBoundingClientRect();
    const viewportH = window.innerHeight;
    const estimatedMenuH = 140 + (extraActions?.length || 0) * 38; // conservative estimate
    const spaceBelow = viewportH - rect.bottom;
    const spaceAbove = rect.top;
    // Prefer opening down. Flip up only if there's clearly more room above
    // and the down direction would overflow the viewport.
    setOpenUp(spaceBelow < estimatedMenuH && spaceAbove > spaceBelow);
  }, [open, extraActions]);

  // Hard gate: never render for non-admins.
  if (!user?.is_admin) return null;

  const handleDelete = async () => {
    const confirmed = window.confirm("Are you sure you want to permanently delete this? This cannot be undone.");
    if (!confirmed) return;
    setBusy(true);
    // Map resourceType to its backend collection segment (handles irregular plurals)
    const PLURAL_MAP = {
      "category": "categories",
    };
    const segment = PLURAL_MAP[resourceType] || `${resourceType}s`;
    try {
      await axios.delete(`${API}/admin/moderation/${segment}/${resourceId}`);
      toast.success("Deleted");
      setOpen(false);
      if (onDeleted) onDeleted();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to delete");
    } finally {
      setBusy(false);
    }
  };

  const buttonSize = size === "lg" ? "w-9 h-9" : "w-7 h-7";
  const iconSize = size === "lg" ? "w-4 h-4" : "w-3.5 h-3.5";
  const tid = testIdPrefix ? `${testIdPrefix}-admin-manage` : `admin-manage-${resourceType}-${resourceId}`;

  return (
    <div ref={wrapperRef} className="relative inline-block" onClick={(e) => e.stopPropagation()}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label="Admin manage menu"
        title="Admin manage"
        className={`inline-flex items-center justify-center ${buttonSize} rounded-full text-[#94A3B8] hover:bg-[#F0ECF7] hover:text-[#6B4E8A] transition-colors`}
        data-testid={tid}
      >
        <MoreVertical className={iconSize} />
      </button>
      {open && (
        <div
          ref={popoverRef}
          className={`absolute z-[60] ${openUp ? "bottom-full mb-1" : "top-full mt-1"} ${align === "left" ? "left-0" : "right-0"} min-w-[180px] max-w-[calc(100vw-32px)] max-h-[min(60vh,360px)] overflow-y-auto bg-white border border-[#E5DFEC] rounded-xl shadow-lg py-1 text-sm`}
          role="menu"
          data-testid={`${tid}-popover`}
        >
          {extraActions.map((action, i) => {
            const Icon = action.icon || Pencil;
            return (
              <button
                key={action.label || `action-${i}`}
                type="button"
                role="menuitem"
                disabled={busy}
                onClick={() => { setOpen(false); action.onClick && action.onClick(); }}
                className="w-full text-left px-3 py-2 text-[#475569] hover:bg-[#FAF7F2] inline-flex items-center gap-2"
                data-testid={`${tid}-action-${i}`}
              >
                <Icon className="w-3.5 h-3.5" />
                {action.label}
              </button>
            );
          })}
          {extraActions.length > 0 && <div className="border-t border-[#E5DFEC] my-1" />}
          <button
            type="button"
            role="menuitem"
            disabled={busy}
            onClick={handleDelete}
            className="w-full text-left px-3 py-2 text-[#EF4444] hover:bg-[#FEE2E2] inline-flex items-center gap-2 disabled:opacity-50"
            data-testid={`${tid}-delete`}
            title={`Delete ${resourceLabel}`}
          >
            <Trash2 className="w-3.5 h-3.5" />
            Delete permanently
          </button>
        </div>
      )}
    </div>
  );
};

export default AdminManageMenu;
